import classNames from 'classnames';
import SwiftTracking from 'components/ui/SwiftTracking/SwiftTracking';
import {ITracking} from 'api/transactions';
import {translate} from 'helpers/localize';
import styles from './PaymentTimeline.module.scss';

type TimelineEntryStatus = 'done' | 'pending' | 'waiting' | 'inactive';

export interface ITimelineEntry {
    status: TimelineEntryStatus;
    label: React.ReactNode;
    comment: React.ReactNode;
}

export interface IPaymentTimelineProps {
    entries: ITimelineEntry[];
    swiftTracking?: ITracking;
    size?: 'default' | 'xl';
}

const overWriteStatus = (
    current: ITimelineEntry,
    prev?: ITimelineEntry,
    next?: ITimelineEntry,
): {status: TimelineEntryStatus; current: boolean; last: boolean} => {
    const result = {
        status: current.status,
        current: false,
        last: false,
    };

    if (prev && prev.status !== 'done') result.status = 'inactive';

    if (!prev) {
        result.last = !next;
    }

    if (current.status === 'pending' && (!prev || prev.status === 'done')) {
        result.current = true;
    }

    if (!next) {
        result.last = true;
    }

    return result;
};

const PaymentTimeline = ({
    entries,
    size,
    swiftTracking,
}: IPaymentTimelineProps) => {
    if (entries.length === 0)
        return (
            <div className={styles.empty} data-notranslate>
                {translate('No tracking information found.')}
            </div>
        );

    return (
        <div className={styles.timeLine}>
            {entries.map((entry, idx) => {
                const {status, current, last} = overWriteStatus(
                    entry,
                    entries[idx - 1],
                    entries[idx + 1],
                );
                return (
                    <div
                        className={classNames(
                            styles.timeLineEntry,
                            styles[status],
                            {
                                [styles.currentStep]: current,
                                [styles.last]: last,
                                [styles.xl]: size === 'xl',
                            },
                        )}
                        key={Math.random().toString()}
                        data-notranslate
                    >
                        <p className={styles.timeLineLabel} data-notranslate>
                            {entry.label}
                        </p>
                        <p className={styles.timeLineComment}>
                            {entry.comment}
                        </p>
                        {idx === entries.length - 2 && !!swiftTracking ? (
                            <SwiftTracking swiftTracking={swiftTracking} />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};

export default PaymentTimeline;
